import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Inbox, Sparkles, Link2, X, Loader2, Users, Search,
} from "lucide-react";

const FILTERS = [["pending", "Pending"], ["fulfilled", "Fulfilled"], ["declined", "Declined"], ["all", "All"]];
const STATUS_CLS = {
  pending:   "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30",
  fulfilled: "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30",
  declined:  "bg-secondary text-muted-foreground border-border",
};

export default function AdminRoleRequests() {
  const [requests, setRequests] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");

  const [genTarget, setGenTarget] = useState(null);
  const [genIndustry, setGenIndustry] = useState("");
  const [genFamily, setGenFamily] = useState("");
  const [genBusy, setGenBusy] = useState(false);

  const [mapTarget, setMapTarget] = useState(null);
  const [allRoles, setAllRoles] = useState([]);
  const [roleQuery, setRoleQuery] = useState("");
  const [mapBusy, setMapBusy] = useState(false);

  const [declineTarget, setDeclineTarget] = useState(null);
  const [declineReason, setDeclineReason] = useState("");
  const [declineBusy, setDeclineBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/role-requests", { params: { status: filter } });
      setRequests(data.requests || []);
      setPendingCount(data.pending_count || 0);
    } catch { toast.error("Could not load role requests"); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const openGenerate = (r) => { setGenTarget(r); setGenIndustry(""); setGenFamily(""); };
  const doGenerate = async () => {
    setGenBusy(true);
    try {
      const { data } = await api.post(`/admin/role-requests/${genTarget.request_id}/generate`,
        { industry: genIndustry.trim() || null, family: genFamily.trim() || null });
      toast.success(`Created "${data.name}" — requesters notified`);
      setGenTarget(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate role"); }
    finally { setGenBusy(false); }
  };

  const openMap = async (r) => {
    setMapTarget(r); setRoleQuery("");
    if (allRoles.length === 0) {
      try { const { data } = await api.get("/admin/roles"); setAllRoles(data || []); } catch { /* */ }
    }
  };
  const doMap = async (roleId) => {
    setMapBusy(true);
    try {
      await api.post(`/admin/role-requests/${mapTarget.request_id}/map`, { role_id: roleId });
      toast.success("Request mapped — requesters notified");
      setMapTarget(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not map request"); }
    finally { setMapBusy(false); }
  };

  const doDecline = async () => {
    setDeclineBusy(true);
    try {
      await api.post(`/admin/role-requests/${declineTarget.request_id}/decline`,
        { reason: declineReason.trim() || null });
      toast.success("Request declined");
      setDeclineTarget(null); setDeclineReason(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not decline request"); }
    finally { setDeclineBusy(false); }
  };

  const filteredRoles = allRoles
    .filter((r) => r.name.toLowerCase().includes(roleQuery.toLowerCase()))
    .slice(0, 40);

  return (
    <main className="max-w-6xl mx-auto px-6 md:px-12 py-12">
      {/* Header */}
      <div className="mb-8">
        <Link to="/admin" className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-[#1D4ED8] inline-flex items-center gap-1.5 transition-colors group">
          <ArrowLeft className="w-3 h-3 group-hover:-translate-x-0.5 transition-transform" /> Admin
        </Link>
        <h1 className="font-display font-black text-4xl tracking-tight text-[#0F172A] mt-2 flex items-center gap-3">
          <Inbox className="w-8 h-8 text-[#1D4ED8]" /> Role Requests
          {pendingCount > 0 && (
            <span className="font-mono text-xs px-2 py-1 rounded-full bg-[#F59E0B]/15 text-[#F59E0B] font-bold">
              {pendingCount} pending
            </span>
          )}
        </h1>
        <p className="text-muted-foreground mt-1 max-w-2xl">
          Roles candidates asked for that aren't in the catalog (free users, and premium low-confidence
          or over-cap). Generate, map to an existing role, or decline — requesters are emailed when it's live.
        </p>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-5">
        {FILTERS.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setFilter(k)}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold border transition-all ${
              filter === k ? "bg-[#1D4ED8] border-[#1D4ED8] text-white" : "border-border text-muted-foreground hover:border-[#1D4ED8]/40"
            }`}>
            {label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-xl border border-border bg-white overflow-hidden">
        {loading ? (
          <div className="p-12 font-mono text-sm text-muted-foreground">Loading…</div>
        ) : requests.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <Inbox className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="font-semibold">No {filter === "all" ? "" : filter} requests.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium">Requested role</th>
                <th className="text-left px-3 py-2.5 font-medium">Confidence</th>
                <th className="text-center px-3 py-2.5 font-medium">Requesters</th>
                <th className="text-left px-3 py-2.5 font-medium">Status</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.request_id} className="border-t border-border/60 hover:bg-secondary/20">
                  <td className="px-4 py-3">
                    <div className="font-semibold">{r.title}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">
                      {r.soc_code || "no SOC"}{r.tiers?.length ? ` · ${r.tiers.join(", ")}` : ""}
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-lg border ${
                      r.confidence === "high" ? "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30" : "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30"
                    }`}>{r.confidence || "low"}</span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className="inline-flex items-center gap-1 font-mono text-xs text-muted-foreground">
                      <Users className="w-3 h-3" /> {r.requester_count ?? (r.requesters?.length || 0)}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-lg border ${STATUS_CLS[r.status] || STATUS_CLS.declined}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    {r.status === "pending" ? (
                      <div className="inline-flex gap-2">
                        <Button size="sm" onClick={() => openGenerate(r)}
                          className="rounded-xl bg-[#10B981] hover:bg-[#059669] h-8 px-2.5 text-xs">
                          <Sparkles className="w-3.5 h-3.5 mr-1" /> Generate
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openMap(r)}
                          className="rounded-xl h-8 px-2.5 text-xs">
                          <Link2 className="w-3.5 h-3.5 mr-1" /> Map
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setDeclineTarget(r)}
                          className="rounded-xl h-8 px-2.5 text-xs hover:bg-[#E11D48] hover:text-white hover:border-[#E11D48]">
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ) : r.resolved_role_id ? (
                      <Link to="/admin/roles" className="font-mono text-[10px] text-[#1D4ED8] hover:underline">
                        {r.resolved_role_id}
                      </Link>
                    ) : <span className="font-mono text-[10px] text-muted-foreground">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Generate modal */}
      <Dialog open={!!genTarget} onOpenChange={(o) => { if (!o) setGenTarget(null); }}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-display font-bold">Generate "{genTarget?.title}"</DialogTitle>
            <DialogDescription>
              Builds a full O*NET-grounded question bank and publishes the role live. Leave the fields
              blank to auto-categorize as “General”.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold mb-1.5">Industry <span className="text-muted-foreground font-normal">(optional)</span></label>
              <Input value={genIndustry} onChange={(e) => setGenIndustry(e.target.value)} className="rounded-xl" placeholder="e.g. Technology" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">Job family <span className="text-muted-foreground font-normal">(optional)</span></label>
              <Input value={genFamily} onChange={(e) => setGenFamily(e.target.value)} className="rounded-xl" placeholder="e.g. Engineering" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setGenTarget(null)}>Cancel</Button>
            <Button onClick={doGenerate} disabled={genBusy} className="rounded-xl bg-[#10B981] hover:bg-[#059669]">
              {genBusy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate &amp; publish</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Map modal */}
      <Dialog open={!!mapTarget} onOpenChange={(o) => { if (!o) setMapTarget(null); }}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-display font-bold">Map "{mapTarget?.title}" to an existing role</DialogTitle>
            <DialogDescription>Pick the role to point this request at. Requesters will be emailed.</DialogDescription>
          </DialogHeader>
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input value={roleQuery} onChange={(e) => setRoleQuery(e.target.value)}
              placeholder="Search roles…" className="rounded-xl pl-9" autoFocus />
          </div>
          <div className="max-h-64 overflow-y-auto rounded-xl border border-border divide-y divide-border/60">
            {filteredRoles.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground text-center">No matching roles.</div>
            ) : filteredRoles.map((role) => (
              <button key={role.role_id} type="button" disabled={mapBusy}
                onClick={() => doMap(role.role_id)}
                className="w-full text-left px-3 py-2.5 hover:bg-[#1D4ED8]/5 transition-colors flex items-center justify-between gap-2 disabled:opacity-50">
                <span className="text-sm font-medium truncate">{role.name}</span>
                <span className="font-mono text-[10px] text-muted-foreground">{role.role_id}</span>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setMapTarget(null)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decline modal */}
      <Dialog open={!!declineTarget} onOpenChange={(o) => { if (!o) { setDeclineTarget(null); setDeclineReason(""); } }}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-display font-bold text-[#E11D48]">Decline "{declineTarget?.title}"</DialogTitle>
            <DialogDescription>Marks the request declined. Requesters are not emailed.</DialogDescription>
          </DialogHeader>
          <Input value={declineReason} onChange={(e) => setDeclineReason(e.target.value)}
            placeholder="Reason (optional, internal)" className="rounded-xl" />
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setDeclineTarget(null)}>Cancel</Button>
            <Button onClick={doDecline} disabled={declineBusy} className="rounded-xl bg-[#E11D48] hover:bg-[#BE123C]">
              {declineBusy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Declining…</> : "Decline request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
